import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import Select from 'common-ui/select/Select';
import {
  getAdministrativeDistricts,
  getAdministrativeRegions,
  getCities,
  getStreets,
} from 'features/newsales/newsalesActions';

import { ModalFormContent } from '../Addresses.styles';

const BuildingModal = ({ isOpen, onClose, onSubmit, building, countries, loading }) => {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedStreet, setSelectedStreet] = useState(null);
  const [selectedAdministrativeDistrict, setSelectedAdministrativeDistrict] = useState(null);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [streets, setStreets] = useState([]);
  const [administrativeDistricts, setAdministrativeDistricts] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingStreets, setLoadingStreets] = useState(false);
  const [loadingAdministrativeDistricts, setLoadingAdministrativeDistricts] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (building) {
      setName(building.name || '');
      if (building.countryId && countries) {
        const country = countries.find((c) => c.id === building.countryId);
        if (country) {
          setSelectedCountry({ value: country.id, label: country.name });
          fetchRegions(building.countryId);
        }
      }
    } else {
      setName('');
      setSelectedCountry(null);
      setSelectedRegion(null);
      setSelectedCity(null);
      setSelectedStreet(null);
      setSelectedAdministrativeDistrict(null);
      setRegions([]);
      setCities([]);
      setStreets([]);
      setAdministrativeDistricts([]);
    }
    setError('');
  }, [building, countries, isOpen]);

  const fetchRegions = async (countryId) => {
    if (!countryId) {
      setRegions([]);
      return;
    }
    setLoadingRegions(true);
    try {
      const result = await dispatch(
        getAdministrativeRegions({
          countryId,
          limit: 1000,
        })
      );
      if (result.payload && result.payload.data) {
        setRegions(result.payload.data);
        if (building && building.districtId) {
          const region = result.payload.data.find((r) => r.id === building.districtId);
          if (region) {
            setSelectedRegion({ value: region.id, label: region.name });
            fetchCities(countryId, building.districtId);
          }
        }
      }
    } catch (error) {
      setRegions([]);
    } finally {
      setLoadingRegions(false);
    }
  };

  const fetchCities = async (countryId, districtId) => {
    if (!countryId || !districtId) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    try {
      const result = await dispatch(
        getCities({
          countryId,
          districtId,
          limit: 1000,
        })
      );
      if (result.payload && result.payload.data) {
        setCities(result.payload.data);
        if (building && building.cityId) {
          const city = result.payload.data.find((c) => c.id === building.cityId);
          if (city) {
            setSelectedCity({ value: city.id, label: city.name });
            fetchStreetsAndAdministrativeDistricts(countryId, districtId, building.cityId);
          }
        }
      }
    } catch (error) {
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchStreetsAndAdministrativeDistricts = async (countryId, districtId, cityId) => {
    if (!countryId || !districtId || !cityId) {
      setStreets([]);
      setAdministrativeDistricts([]);
      return;
    }

    setLoadingStreets(true);
    setLoadingAdministrativeDistricts(true);

    try {
      const [streetsResult, administrativeDistrictsResult] = await Promise.all([
        dispatch(
          getStreets({
            countryId,
            districtId,
            cityId,
            limit: 1000,
          })
        ),
        dispatch(
          getAdministrativeDistricts({
            countryId,
            districtId,
            cityId,
            limit: 1000,
          })
        ),
      ]);

      if (streetsResult.payload && streetsResult.payload.data) {
        setStreets(streetsResult.payload.data);
        if (building && building.streetId) {
          const street = streetsResult.payload.data.find((s) => s.id === building.streetId);
          if (street) {
            setSelectedStreet({ value: street.id, label: street.name });
          }
        }
      }

      if (
        administrativeDistrictsResult.payload &&
        administrativeDistrictsResult.payload.data
      ) {
        setAdministrativeDistricts(administrativeDistrictsResult.payload.data);
        if (building && building.administrativeDistrictId) {
          const administrativeDistrict = administrativeDistrictsResult.payload.data.find(
            (ad) => ad.id === building.administrativeDistrictId
          );
          if (administrativeDistrict) {
            setSelectedAdministrativeDistrict({
              value: administrativeDistrict.id,
              label: administrativeDistrict.name,
            });
          }
        }
      }
    } catch (error) {
      setStreets([]);
      setAdministrativeDistricts([]);
    } finally {
      setLoadingStreets(false);
      setLoadingAdministrativeDistricts(false);
    }
  };

  const handleCountryChange = (option) => {
    setSelectedCountry(option);
    setSelectedRegion(null);
    setSelectedCity(null);
    setSelectedStreet(null);
    setSelectedAdministrativeDistrict(null);
    setError('');
    if (option) {
      fetchRegions(option.value);
    } else {
      setRegions([]);
      setCities([]);
      setStreets([]);
      setAdministrativeDistricts([]);
    }
  };

  const handleRegionChange = (option) => {
    setSelectedRegion(option);
    setSelectedCity(null);
    setSelectedStreet(null);
    setSelectedAdministrativeDistrict(null);
    setError('');
    if (option && selectedCountry) {
      fetchCities(selectedCountry.value, option.value);
    } else {
      setCities([]);
      setStreets([]);
      setAdministrativeDistricts([]);
    }
  };

  const handleCityChange = (option) => {
    setSelectedCity(option);
    setSelectedStreet(null);
    setSelectedAdministrativeDistrict(null);
    setError('');
    if (option && selectedCountry && selectedRegion) {
      fetchStreetsAndAdministrativeDistricts(
        selectedCountry.value,
        selectedRegion.value,
        option.value
      );
    } else {
      setStreets([]);
      setAdministrativeDistricts([]);
    }
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Building name is required');
      return;
    }

    if (!selectedCountry) {
      setError('Please select a country');
      return;
    }

    if (!selectedRegion) {
      setError('Please select a region');
      return;
    }

    if (!selectedCity) {
      setError('Please select a city');
      return;
    }

    if (!selectedStreet) {
      setError('Please select a street');
      return;
    }

    const submitData = {
      name: name.trim(),
      countryId: selectedCountry.value,
      districtId: selectedRegion.value,
      cityId: selectedCity.value,
      streetId: selectedStreet.value,
    };

    if (selectedAdministrativeDistrict) {
      submitData.administrativeDistrictId = selectedAdministrativeDistrict.value;
    }

    onSubmit(submitData);
  };

  const handleClose = () => {
    setName('');
    setSelectedCountry(null);
    setSelectedRegion(null);
    setSelectedCity(null);
    setSelectedStreet(null);
    setSelectedAdministrativeDistrict(null);
    setRegions([]);
    setCities([]);
    setStreets([]);
    setAdministrativeDistricts([]);
    setError('');
    onClose();
  };

  const countryOptions = countries
    ? countries.map((country) => ({
        value: country.id,
        label: country.name,
      }))
    : [];

  const regionOptions = regions.map((region) => ({
    value: region.id,
    label: region.name,
  }));

  const cityOptions = cities.map((city) => ({
    value: city.id,
    label: city.name,
  }));

  const streetOptions = streets.map((street) => ({
    value: street.id,
    label: street.name,
  }));

  const administrativeDistrictOptions = administrativeDistricts.map((district) => ({
    value: district.id,
    label: district.name,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onOk={handleSubmit}
      title={building ? 'Edit Building/House' : 'Create Building/House'}
      okText={building ? 'Update' : 'Create'}
      cancelText="Cancel"
      footer
      closeIcon
      width="450px"
      onOkLoading={loading}
    >
      <ModalFormContent>
        <Select
          label="Country"
          req
          value={selectedCountry}
          onChange={handleCountryChange}
          options={countryOptions}
          placeholder="Select a country"
          isClearable
        />
        <Select
          label="Region"
          req
          value={selectedRegion}
          onChange={handleRegionChange}
          options={regionOptions}
          placeholder={
            loadingRegions
              ? 'Loading regions...'
              : selectedCountry
                ? 'Select a region'
                : 'Select a country first'
          }
          isClearable
          isDisabled={!selectedCountry || loadingRegions}
        />
        <Select
          label="City/Village"
          req
          value={selectedCity}
          onChange={handleCityChange}
          options={cityOptions}
          placeholder={
            loadingCities
              ? 'Loading cities...'
              : selectedRegion
                ? 'Select a city'
                : 'Select a region first'
          }
          isClearable
          isDisabled={!selectedRegion || loadingCities}
        />
        <Select
          label="Street"
          req
          value={selectedStreet}
          onChange={(option) => {
            setSelectedStreet(option);
            setError('');
          }}
          options={streetOptions}
          placeholder={
            loadingStreets
              ? 'Loading streets...'
              : selectedCity
                ? 'Select a street'
                : 'Select a city first'
          }
          isClearable
          isDisabled={!selectedCity || loadingStreets}
        />
        <Select
          label="Administrative District"
          value={selectedAdministrativeDistrict}
          onChange={(option) => {
            setSelectedAdministrativeDistrict(option);
            setError('');
          }}
          options={administrativeDistrictOptions}
          placeholder={
            loadingAdministrativeDistricts
              ? 'Loading administrative districts...'
              : selectedCity
                ? 'Select (optional)'
                : 'Select a city first'
          }
          isClearable
          isDisabled={!selectedCity || loadingAdministrativeDistricts}
        />
        <Input
          label="Building/House Name"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError('');
          }}
          error={error}
          fullWidth
          placeholder="Enter building/house name"
        />
      </ModalFormContent>
    </Modal>
  );
};

export default BuildingModal;
